import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {BuyFeatureButtonsTypeToDisable, BuyFeatureView} from './BuyFeatureView';
import {GlobalEventsMapForBackwardCompatibility, UIElements} from '@gongxigames/framework/slot/events';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {CoreDI, CurrencyFormatter, ModuleIsEnabledFilter, inject, ResourcesPack, LockManager} from '@gongxigames/framework/core';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {isEnabledBuyFeature} from '@gongxigames/framework/slot/modules/buyFeature/isEnabledBuyFeature';
import {BuyFeatureStates} from '@gongxigames/framework/slot/modules/buyFeature/BuyFeatureStates';
import {AutoplayModule} from '@gongxigames/framework/slot/modules/autoplay/AutoplayModule';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {
    ILogic,
    ILogicBuyFeatureFunctional,
    UIButtons,
    WinMeterEvents
} from '@gongxigames/framework/slot';
import {BuyFeatureFeatureStatesMap} from '@gongxigames/framework/slot/modules/buyFeature/states/BuyFeatureFeatureStatesMap';

export type BuyFeaturePopupEvents = {
    'BuyFeature.Popup.Open': void;
    'BuyFeature.Popup.Yes': void;
    'BuyFeature.Popup.No': void;
    'BuyFeature.Popup.Close': void;
} & GlobalEventsMapForBackwardCompatibility;

const buyFeatureShowPopupButton = 'buyFeatureShowPopupButton';
const buyFeatureConfirmButton = 'buyFeatureConfirmButton';

export class BuyFeatureModule extends Module<BuyFeatureView, BuyFeaturePopupEvents> {
    public static readonly NAME: symbol = Symbol('BuyFeatureModule');
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledBuyFeature;
    public static readonly FEATURE_PRICE_UPDATE: symbol = Symbol('FEATURE_PRICE_UPDATE');

    @inject(CoreDI.CurrencyFormatter)
    protected currencyFormatter!: CurrencyFormatter;

    private lockManager: LockManager = new LockManager();

    public init(): void {
        super.init();

        this.lockManager.add(buyFeatureShowPopupButton, this.onChangeLockButton.bind(this, 'show'));
        this.lockManager.add(buyFeatureConfirmButton, this.onChangeLockButton.bind(this, 'confirm'));

        this.eventManager.emit('General.UI.RepositionElements', [{
            name: '_totalBetOpenButton',
            x: 1761
        },
        {
            name: '_betIncButton',
            x: 1775
        },
        {
            name: '_betDecButton',
            x: 1775
        },
        {
            name: 'turboButtonOn',
            x: 1400
        },
        {
            name: 'turboButtonOff',
            x: 1400
        }]
        );

        this.setBuyFeatureVisibility();
    }

    protected setBuyFeatureVisibility(): void {
        if (this.logic.shouldHideBuyFeature()) {
            this.gotoState(BuyFeatureStates.HIDDEN);
        } else {
            this.gotoState(BuyFeatureStates.BASIC);
        }
    }

    protected onPromotionStarted(): void {
        this.gotoState(BuyFeatureStates.HIDDEN);
    }

    protected onPromotionEnded(): void {
        this.gotoState(BuyFeatureStates.BASIC);
    }

    public getResourcesToLoad(): ResourcesPack {
        return {
            default: 'default_priority_buy_feature',
        };
    }

    private onChangeLockButton(type: BuyFeatureButtonsTypeToDisable, lock = true): void {
        if (lock) {
            this.view.disableButton(type);
        } else {
            this.view.enableButton(type);
        }
    }

    public restore(): void {
        if (!this.logic.nextActionIsSpin()) {
            const { isProgressStarted } = this.getLogic<ILogic & ILogicBuyFeatureFunctional>().getBuyFeature();
            const shouldHideBuyFeature = this.logic.shouldHideBuyFeature();

            if (isProgressStarted || shouldHideBuyFeature) {
                this.gotoState(BuyFeatureStates.HIDDEN);
            } else {
                this.gotoState(BuyFeatureStates.WAITING);
            }
        }
    }

    protected setupStates(): void {
        this.addStates(BuyFeatureFeatureStatesMap);
        this.gotoState(BuyFeatureStates.BASIC);
    }

    protected setupViews(): void {
        this.setView(BuyFeatureView.NAME);
        this.updateButtonsState();
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle(LogicEvents.SPIN_RESPONSE);
        this.onCurrentStateHandle(FlowModuleEvents.ENTER_BASIC_STATE);
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(ReelsEvents.SPIN_ENDED);
        this.onCurrentStateHandle('General.Popups.Add');

        this.on('General.UI.ENABLE', this.enableButtonListener);
        this.on('General.UI.DISABLE', this.disableButtonListener);
        this.on('General.UI.BET.CHANGED', this.updateButtonsState);
        this.on('General.UI.BALANCE.UPDATED', this.updateButtonsState);
        this.on('General.Promotion.Started', this.onPromotionStarted);
        this.on('General.Promotion.Ended', this.onPromotionEnded);
        this.on('General.Flow.PAID_ROUND_ENDED', this.setBuyFeatureVisibility);
        this.on('Autoplay.STARTED', () => this.disableShowPopupButtonByName(AutoplayModule.NAME));
        this.on('Autoplay.STOPPED', () => this.enableShowPopupButtonByName(AutoplayModule.NAME));
        this.on(WinMeterEvents.COUNT_UP, () => this.lockManager.lock(buyFeatureShowPopupButton, 'COUNT_UP'));
        this.on('General.UI.BALANCE.UPDATED', () => this.lockManager.unlock(buyFeatureShowPopupButton, 'COUNT_UP'));

        this.onView('BuyFeature.Popup.Open', this.onBuyFeaturePopupOpen);
        this.onView('BuyFeature.Popup.Yes', this.onBuyFeaturePopupYes);
        this.onView('BuyFeature.Popup.No', this.onBuyFeaturePopupNo);
        this.onView('BuyFeature.Popup.Close', this.onBuyFeaturePopupClose);
    }

    protected onBuyFeaturePopupOpen(): void {
        this.eventManager.emit('General.UI.DISABLE', BuyFeatureModule.NAME);
        this.eventManager.emit('General.UI.Button.ENABLE', {
            name: UIButtons.BET,
            reason: BuyFeatureModule.NAME
        });
        this.eventManager.emit('General.UI.Button.ENABLE', {
            name: UIButtons.TOTAL_BET_OPEN_BUTTON,
            reason: BuyFeatureModule.NAME
        });
        this.eventManager.emit('General.UI.SWAP_LAYER', {
            layer: LAYERS.POPUPS,
            element: UIElements.TOTAL_BET_PANEL
        });

        this.gotoState(BuyFeatureStates.FEATURE);
        this.eventManager.emit('General.BuyFeature.OPEN');
    }

    protected onBuyFeaturePopupYes(): void {
        this.eventManager.emit('General.UI.ENABLE', BuyFeatureModule.NAME);
        this.eventManager.emit('General.BuyFeature.BUY');
    }

    protected onBuyFeaturePopupNo(): void {
        this.eventManager.emit('General.UI.ENABLE', BuyFeatureModule.NAME);

        this.gotoState(BuyFeatureStates.BASIC);
        this.eventManager.emit('General.BuyFeature.DECLINE');
    }

    protected onBuyFeaturePopupClose(): void {
        this.eventManager.emit('General.UI.DISABLE', BuyFeatureModule.NAME);

        this.eventManager.emit('General.UI.SWAP_LAYER', {
            layer: LAYERS.KEYPAD,
            element: UIElements.TOTAL_BET_PANEL
        });
        this.eventManager.emit('General.BuyFeature.CLOSE');
    }

    protected updateButtonsState(): void {
        const price = this.logic.getBuyFeaturePrice();

        if (this.logic.canSpin(price) && this.logic.canBuyFeature()) {
            this.enableShowPopupButtonByName(BuyFeatureModule.FEATURE_PRICE_UPDATE);

            if (this.lockManager.getLock(buyFeatureConfirmButton)) {
                this.lockManager.unlock(buyFeatureConfirmButton, BuyFeatureModule.FEATURE_PRICE_UPDATE);
            }
        } else {
            this.disableShowPopupButtonByName(BuyFeatureModule.FEATURE_PRICE_UPDATE);
            this.lockManager.lock(buyFeatureConfirmButton, BuyFeatureModule.FEATURE_PRICE_UPDATE);
        }

        this.view?.updatePrice(this.currencyFormatter.formatCents(price));
    }

    protected enableButtonListener(eventID: symbol | string, reason: symbol): void {
        this.enableShowPopupButtonByName(reason);
    }

    protected disableButtonListener(eventID: symbol | string, reason: symbol): void {
        this.disableShowPopupButtonByName(reason);
    }

    public enableShowPopupButtonByName(reason: symbol): void {
        if (this.lockManager.getLock(buyFeatureShowPopupButton)) {
            this.lockManager.unlock(buyFeatureShowPopupButton, reason);
        }
    }

    public disableShowPopupButtonByName(reason: symbol): void {
        this.lockManager.lock(buyFeatureShowPopupButton, reason);
    }
}
